import _ from 'lodash';
import { EDGE_DEFAULT_THICKNESS } from '../graphConstants';
import type { GraphEdgeSettingsStyle } from '../graphSettings';
import { type RGBColorNumber, rgbStringToNumber } from '../utils/rgb';
import { getEdgeThickness } from './getEdgeThickness';

export type LinePattern = 'solid' | 'dashed' | 'dotted';

export function getEdgeStyle(
  style: GraphEdgeSettingsStyle,
  groupLength: number,
  overwrite?: EdgeStyle,
): EdgeStyle {
  const { color, pattern, thicknessThresholds } = style;
  return _.merge(
    {
      line: {
        pattern,
        color: rgbStringToNumber(color),
        thickness: getEdgeThickness(thicknessThresholds, groupLength),
      },
    },
    overwrite,
  );
}

/**
 * See constants below for some default values.
 * It makes no sense to define default EdgeStyle object
 * since its shape depends on edge type - there is
 * no sane default whether the edge should have a circle
 * and which arrows it has.
 */
export interface EdgeStyle {
  circle?: {
    // not present = don't draw a circle
    color?: RGBColorNumber;
  };
  arrowHeads?: {
    source?: {
      color?: RGBColorNumber;
    };
    target?: {
      color?: RGBColorNumber;
    };
  };
  line?: {
    color?: RGBColorNumber;
    pattern?: LinePattern;
    thickness?: number;
  };
}

/**
 * Default color for anything on the edge - circle, arrowHeads, line
 */
export const edgeDefaultColor: RGBColorNumber = 0xa0a0a0;
export const edgeDefaultLinePattern: LinePattern = 'solid';
export const edgeDefaultThickness = EDGE_DEFAULT_THICKNESS;

// following functions allow undefined argument to be able to directly process EdgeData.style

export const getEdgeStyleThickness = (style?: EdgeStyle): number =>
  style?.line?.thickness ?? edgeDefaultThickness;

export const getEdgeStyleLinePattern = (style?: EdgeStyle): LinePattern =>
  style?.line?.pattern ?? edgeDefaultLinePattern;

export const getEdgeStyleLineColor = (style?: EdgeStyle): RGBColorNumber =>
  style?.line?.color ?? edgeDefaultColor;

export const getEdgeStyleArrowHeadColor = (
  position: 'source' | 'target',
  style?: EdgeStyle,
): RGBColorNumber =>
  style?.arrowHeads?.[position]?.color ?? getEdgeStyleLineColor(style);

export const getEdgeStyleCircleColor = (style?: EdgeStyle): RGBColorNumber =>
  style?.circle?.color ?? getEdgeStyleLineColor(style);
